import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { LogIn, LogOut, Coffee, Play, Clock, CheckCircle2, AlertCircle, QrCode } from 'lucide-react';

export default function TimeclockPage({ embedded = false }) {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || '';
  const [codeValid, setCodeValid] = useState(null);

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [pin, setPin] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [empData, setEmpData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (embedded) {
      setCodeValid(true);
      axios.get(`${API}/employees`).then(r => setEmployees((r.data || []).filter(e => e.is_active !== false && e.role !== 'atelier'))).catch(() => setEmployees([]));
      return;
    }
    if (!code) { setCodeValid(false); return; }
    axios.get(`${API}/public/timeclock/verify-code`, { params: { code } })
      .then(() => { setCodeValid(true); return axios.get(`${API}/public/timeclock/employees`); })
      .then(r => setEmployees(r.data))
      .catch(() => setCodeValid(false));
  }, [code, embedded]);

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (pin.length === 4 && selectedEmployee && !loading && !authenticated) {
      verifyPin();
    }
  }, [pin]);

  const verifyPin = async () => {
    if (!selectedEmployee || pin.length !== 4) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/timeclock/verify-pin`, { employee_id: selectedEmployee, pin_code: pin });
      setEmpData(res.data);
      setAuthenticated(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code PIN incorrect');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const reset = useCallback(() => {
    setAuthenticated(false);
    setEmpData(null);
    setPin('');
    setSelectedEmployee('');
    setConfirmation(null);
  }, []);

  // Auto-reset after confirmation
  useEffect(() => {
    if (!confirmation) return;
    const timer = setTimeout(reset, 4000);
    return () => clearTimeout(timer);
  }, [confirmation, reset]);

  const doAction = async (actionType) => {
    setActionLoading(actionType);
    try {
      const res = await axios.post(`${API}/public/timeclock/action`, {
        employee_id: selectedEmployee, pin_code: pin, action_type: actionType,
      });
      setConfirmation({ action: actionType, message: res.data.message });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading('');
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '--:--';
    return new Date(iso).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
  };

  // === INVALID CODE ===
  if (codeValid === false) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center space-y-6">
          <div className="w-20 h-20 mx-auto bg-red-600/20 rounded-2xl flex items-center justify-center">
            <QrCode className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Acces refuse</h1>
          <p className="text-slate-400">Veuillez scanner le QR code affiche au garage pour acceder a la pointeuse.</p>
        </div>
      </div>
    );
  }

  // === LOADING ===
  if (codeValid === null) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-white text-center">
          <Clock className="w-8 h-8 animate-spin mx-auto mb-3" />
          <p>Verification...</p>
        </div>
      </div>
    );
  }

  // === CONFIRMATION SCREEN (auto-reset after 4s) ===
  if (confirmation) {
    const confConfig = {
      clock_in: { icon: LogIn, color: 'bg-emerald-600', label: 'Entree enregistree' },
      clock_out: { icon: LogOut, color: 'bg-red-600', label: 'Sortie enregistree' },
      break_start: { icon: Coffee, color: 'bg-amber-600', label: 'Pause enregistree' },
      break_end: { icon: Play, color: 'bg-blue-600', label: 'Reprise enregistree' },
    };
    const conf = confConfig[confirmation.action] || confConfig.clock_in;
    const ConfIcon = conf.icon;

    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center space-y-6 animate-in fade-in duration-300">
          <div className={`w-24 h-24 mx-auto ${conf.color} rounded-full flex items-center justify-center shadow-lg`}>
            <ConfIcon className="w-12 h-12 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{conf.label}</h1>
            <p className="text-slate-300 text-lg mt-2">{empData?.first_name} {empData?.last_name}</p>
            <p className="text-slate-500 text-sm mt-1">
              {new Date().toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <div className="h-1 bg-slate-800 rounded-full overflow-hidden mx-10">
            <div className={`h-full ${conf.color} rounded-full animate-shrink`} />
          </div>
          <style>{`@keyframes shrink { from { width: 100%; } to { width: 0%; } } .animate-shrink { animation: shrink 4s linear forwards; }`}</style>
        </div>
      </div>
    );
  }

  // === LOGIN SCREEN ===
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-white rounded-2xl flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-slate-900" />
            </div>
            <h1 className="text-2xl font-bold text-white">Pointeuse</h1>
            <p className="text-slate-400 text-sm mt-1">Vortekio</p>
          </div>

          <Card className="p-6 space-y-4 rounded-2xl">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Employe</label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="tc-employee-select" className="h-12 rounded-xl text-base">
                  <SelectValue placeholder="Choisir..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Code PIN</label>
              {/* PIN dots */}
              <div className="flex justify-center gap-3 mb-4">
                {[0,1,2,3].map(i => (
                  <div key={i} className={`w-4 h-4 rounded-full transition-all ${pin.length > i ? 'bg-slate-900 scale-110' : 'bg-slate-200'}`} />
                ))}
              </div>
              {/* Numeric keypad */}
              <div className="grid grid-cols-3 gap-2">
                {['1','2','3','4','5','6','7','8','9'].map(n => (
                  <Button key={n} type="button" variant="outline"
                    className="h-14 text-xl font-semibold rounded-xl"
                    onClick={() => pin.length < 4 && setPin(p => p + n)}
                    data-testid={`tc-key-${n}`}
                  >{n}</Button>
                ))}
                <Button type="button" variant="ghost"
                  className="h-14 text-sm font-medium rounded-xl text-red-500"
                  onClick={() => setPin('')}
                  data-testid="tc-key-clear"
                >Effacer</Button>
                <Button type="button" variant="outline"
                  className="h-14 text-xl font-semibold rounded-xl"
                  onClick={() => pin.length < 4 && setPin(p => p + '0')}
                  data-testid="tc-key-0"
                >0</Button>
                <Button type="button" variant="ghost"
                  className="h-14 text-lg rounded-xl"
                  onClick={() => setPin(p => p.slice(0, -1))}
                  data-testid="tc-key-back"
                >&larr;</Button>
              </div>
            </div>

            {loading && (
              <div className="flex items-center justify-center gap-2 text-slate-500">
                <Clock className="w-4 h-4 animate-spin" />
                <span className="text-sm">Verification...</span>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  // === ACTION SCREEN ===
  const status = empData?.status || 'absent';

  const actionConfig = {
    absent: { action: 'clock_in', icon: LogIn, color: 'bg-emerald-600 hover:bg-emerald-700', label: 'ENTREE', desc: 'Pointer votre arrivee' },
    active: null, // Two choices: pause or sortie
    on_break: { action: 'break_end', icon: Play, color: 'bg-blue-600 hover:bg-blue-700', label: 'FIN DE PAUSE', desc: 'Reprendre le travail' },
    completed: { action: 'clock_in', icon: LogIn, color: 'bg-emerald-600 hover:bg-emerald-700', label: 'RE-ENTREE', desc: 'Pointer une nouvelle entree' },
  };

  const statusLabels = {
    absent: 'Pas encore pointe',
    active: 'En service',
    on_break: 'En pause',
    completed: 'Journee terminee',
  };

  const statusColors = {
    absent: 'text-slate-400',
    active: 'text-emerald-400',
    on_break: 'text-amber-400',
    completed: 'text-blue-400',
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-5">
        {/* Employee info */}
        <div className="text-center">
          <h1 className="text-xl font-bold text-white">{empData?.first_name} {empData?.last_name}</h1>
          <p className={`text-sm font-medium mt-1 ${statusColors[status]}`}>
            {statusLabels[status]}
          </p>
          {empData?.clock_in && (
            <p className="text-slate-500 text-xs mt-1">
              Entree: {fmtTime(empData.clock_in)}
              {empData?.clock_out && ` — Sortie: ${fmtTime(empData.clock_out)}`}
            </p>
          )}
        </div>

        {/* Action buttons */}
        {status === 'active' ? (
          // Active: choice between PAUSE and SORTIE
          <div className="space-y-3">
            <Button
              onClick={() => doAction('break_start')}
              disabled={!!actionLoading}
              className="w-full h-20 rounded-2xl text-xl font-bold bg-amber-600 hover:bg-amber-700 shadow-lg"
              data-testid="tc-break-start-btn"
            >
              <Coffee className="w-7 h-7 mr-3" />
              {actionLoading === 'break_start' ? 'En cours...' : 'PAUSE'}
            </Button>
            <Button
              onClick={() => doAction('clock_out')}
              disabled={!!actionLoading}
              className="w-full h-20 rounded-2xl text-xl font-bold bg-red-600 hover:bg-red-700 shadow-lg"
              data-testid="tc-clock-out-btn"
            >
              <LogOut className="w-7 h-7 mr-3" />
              {actionLoading === 'clock_out' ? 'En cours...' : 'SORTIE'}
            </Button>
          </div>
        ) : (
          // Single action button
          (() => {
            const cfg = actionConfig[status] || actionConfig.absent;
            const Icon = cfg.icon;
            return (
              <div className="space-y-3">
                <Button
                  onClick={() => doAction(cfg.action)}
                  disabled={!!actionLoading}
                  className={`w-full h-24 rounded-2xl text-2xl font-bold ${cfg.color} shadow-lg`}
                  data-testid={`tc-${cfg.action.replace('_', '-')}-btn`}
                >
                  <Icon className="w-8 h-8 mr-3" />
                  {actionLoading ? 'En cours...' : cfg.label}
                </Button>
                <p className="text-center text-slate-500 text-sm">{cfg.desc}</p>
              </div>
            );
          })()
        )}

        {/* Change employee */}
        <Button
          variant="ghost"
          onClick={reset}
          className="w-full text-slate-500 hover:text-white text-sm"
          data-testid="tc-logout-btn"
        >
          Changer d'employe
        </Button>
      </div>
    </div>
  );
}
